/** Menu tabs, left to right. Pages slide toward whichever side the new tab sits on. */
export const MENU_TABS = ['store', 'friends', 'home', 'events', 'album'] as const;

export type MenuTab = (typeof MENU_TABS)[number];

/** Page slide. Fast, and never a fade. */
export const SLIDE_MS = 200;

/** Popup sheet rising over a menu page. */
export const SHEET_MS = 200;

/** Wood wallet bar across the top of the shell. */
export const WALLET_BAR_H = 108;

/** Bottom tab bar. Matches the glen bar height. */
export const MENU_TAB_H = 118;

export const TAB_ICON_ACTIVE = 84;
export const TAB_ICON_INACTIVE = 52;

let current: MenuTab = 'home';
let shotTaken = false;

export function menuTab(): MenuTab {
  return current;
}

export function setMenuTab(tab: MenuTab): void {
  current = tab;
}

/** A tab left of the current one enters from the left. A tab to the right enters from the right. */
export function slideFrom(from: MenuTab, to: MenuTab): 'left' | 'right' | 'none' {
  const a = MENU_TABS.indexOf(from);
  const b = MENU_TABS.indexOf(to);
  if (a === b || a < 0 || b < 0) return 'none';
  return b < a ? 'left' : 'right';
}

/**
 * Camera scroll that plays the slide.
 * Enter is the incoming camera's starting scrollX. Exit is the outgoing camera's ending scrollX.
 * Content lives at x = 0..width, so a positive scroll parks it off the left.
 */
export function slideOffsets(direction: 'left' | 'right' | 'none', width: number): { enter: number; exit: number } {
  if (direction === 'none') return { enter: 0, exit: 0 };
  if (direction === 'left') return { enter: width, exit: -width };
  return { enter: -width, exit: width };
}

/**
 * Resting tabs are smaller and unlabeled. `muted` is that resting state.
 * The icon stays the painted art either way.
 */
export function tabChrome(id: MenuTab, selected: MenuTab): { active: boolean; icon: number; label: boolean; muted: boolean } {
  const active = id === selected;
  return {
    active,
    icon: active ? TAB_ICON_ACTIVE : TAB_ICON_INACTIVE,
    label: active,
    muted: !active,
  };
}

/**
 * Tab wood in world pixels. Icons sit in a normal band on the canvas edge.
 * The wood also covers the phone's extra strip, so nothing blurred shows under it.
 * `safe` is the only pad.
 */
export function menuBarFrame(
  stageH: number,
  viewH: number,
  safe = 0,
  barH = MENU_TAB_H,
): { woodTop: number; woodBottom: number; bandTop: number; height: number } {
  const bleed = Math.max(0, (viewH - stageH) / 2);
  const woodBottom = stageH + bleed - safe;
  const bandTop = woodBottom - barH;
  const woodTop = Math.min(stageH - safe, bandTop);
  return { woodTop, woodBottom, bandTop, height: woodBottom - woodTop };
}

export function pageKey(tab: MenuTab): 'collection' | 'friends' | 'hub' | 'events' | 'album' {
  if (tab === 'store') return 'collection';
  if (tab === 'home') return 'hub';
  if (tab === 'events') return 'events';
  if (tab === 'friends') return 'friends';
  return 'album';
}

export function sheetMotion(height: number): { fromY: number; toY: number; duration: number } {
  return { fromY: height, toY: 0, duration: SHEET_MS };
}

/** The first menu scene to start consumes the screenshot query so later tab visits do not replay it. */
export function takeMenuShot(): string {
  if (shotTaken) return '';
  shotTaken = true;
  const loc = (globalThis as { location?: { search?: string } }).location;
  if (!loc?.search) return '';
  return new URLSearchParams(loc.search).get('shot') ?? '';
}
